import { __assign, __read, __values } from "tslib";
import * as u from './urx';
import { empty, findMaxKeyValue, rangesWithin } from './AATree';
import { groupedListSystem } from './groupedListSystem';
import { getInitialTopMostItemIndexNumber, initialTopMostItemIndexSystem } from './initialTopMostItemIndexSystem';
import { propsReadySystem } from './propsReadySystem';
import { scrollToIndexSystem } from './scrollToIndexSystem';
import { sizeRangeSystem } from './sizeRangeSystem';
import { originalIndexFromItemIndex, sizeSystem, hasGroups, rangesWithinOffsets } from './sizeSystem';
import { stateFlagsSystem } from './stateFlagsSystem';
import { rangeComparator, tupleComparator } from './comparators';
import { recalcSystem } from './recalcSystem';
function probeItemSet(index, sizes, data) {
    if (hasGroups(sizes)) {
        var itemIndex = originalIndexFromItemIndex(index, sizes);
        var groupIndex = findMaxKeyValue(sizes.groupOffsetTree, itemIndex)[0];
        return [
            { index: groupIndex, size: 0, offset: 0 },
            { index: itemIndex, size: 0, offset: 0, data: data && data[0] },
        ];
    }
    return [{ index: index, size: 0, offset: 0, data: data && data[0] }];
}
var EMPTY_LIST_STATE = {
    items: [],
    topItems: [],
    offsetTop: 0,
    offsetBottom: 0,
    top: 0,
    bottom: 0,
    topListHeight: 0,
    totalCount: 0,
    firstItemIndex: 0,
};
function transposeItems(items, sizes, firstItemIndex) {
    var e_1, _a;
    if (items.length === 0) {
        return [];
    }
    if (!hasGroups(sizes)) {
        return items.map(function (item) { return (__assign(__assign({}, item), { index: item.index + firstItemIndex, originalIndex: item.index })); });
    }
    var startIndex = items[0].index;
    var endIndex = items[items.length - 1].index;
    var transposedItems = [];
    var groupRanges = rangesWithin(sizes.groupOffsetTree, startIndex, endIndex);
    var currentRange = undefined;
    var currentGroupIndex = 0;
    try {
        for (var items_1 = __values(items), items_1_1 = items_1.next(); !items_1_1.done; items_1_1 = items_1.next()) {
            var item = items_1_1.value;
            if (!currentRange || currentRange.end < item.index) {
                currentRange = groupRanges.shift();
                currentGroupIndex = sizes.groupIndices.indexOf(currentRange.start);
            }
            var transposedItem = void 0;
            if (item.index === currentRange.start) {
                transposedItem = {
                    type: 'group',
                    index: currentGroupIndex,
                };
            }
            else {
                transposedItem = {
                    index: item.index - (currentGroupIndex + 1) + firstItemIndex,
                    groupIndex: currentGroupIndex,
                };
            }
            transposedItems.push(__assign(__assign({}, transposedItem), { size: item.size, offset: item.offset, originalIndex: item.index, data: item.data }));
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (items_1_1 && !items_1_1.done && (_a = items_1.return)) _a.call(items_1);
        }
        finally { if (e_1) throw e_1.error; }
    }
    return transposedItems;
}
export function buildListState(items, topItems, totalCount, gap, sizes, firstItemIndex) {
    var lastSize = sizes.lastSize, lastOffset = sizes.lastOffset, lastIndex = sizes.lastIndex;
    var offsetTop = 0;
    var bottom = 0;
    if (items.length > 0) {
        offsetTop = items[0].offset;
        var lastItem = items[items.length - 1];
        bottom = lastItem.offset + lastItem.size;
    }
    var itemCount = totalCount - lastIndex;
    var total = lastOffset + itemCount * lastSize + (itemCount - 1) * gap;
    var top = offsetTop;
    var offsetBottom = total - bottom;
    return {
        items: transposeItems(items, sizes, firstItemIndex),
        topItems: transposeItems(topItems, sizes, firstItemIndex),
        topListHeight: topItems.reduce(function (height, item) { return item.size + height; }, 0),
        offsetTop: offsetTop,
        offsetBottom: offsetBottom,
        top: top,
        bottom: bottom,
        totalCount: totalCount,
        firstItemIndex: firstItemIndex,
    };
}
export function buildListStateFromItemCount(itemCount, initialTopMostItemIndex, sizes, firstItemIndex, gap, data) {
    var e_2, _a;
    var includedGroupsCount = 0;
    if (sizes.groupIndices.length > 0) {
        try {
            for (var _b = __values(sizes.groupIndices), _c = _b.next(); !_c.done; _c = _b.next()) {
                var index = _c.value;
                if (index - includedGroupsCount >= itemCount) {
                    break;
                }
                includedGroupsCount++;
            }
        }
        catch (e_2_1) { e_2 = { error: e_2_1 }; }
        finally {
            try {
                if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
            }
            finally { if (e_2) throw e_2.error; }
        }
    }
    var adjustedCount = itemCount + includedGroupsCount;
    var initialTopMostItemIndexNumber = getInitialTopMostItemIndexNumber(initialTopMostItemIndex, adjustedCount);
    var items = Array.from({ length: adjustedCount }).map(function (_, index) { return ({
        index: index + initialTopMostItemIndexNumber,
        size: 0,
        offset: 0,
        data: data[index + initialTopMostItemIndexNumber],
    }); });
    return buildListState(items, [], adjustedCount, gap, sizes, firstItemIndex);
}
export var listStateSystem = u.system(function (_a) {
    var _b = __read(_a, 8), _c = _b[0], sizes = _c.sizes, totalCount = _c.totalCount, data = _c.data, firstItemIndex = _c.firstItemIndex, gap = _c.gap, groupedListSystem = _b[1], _d = _b[2], visibleRange = _d.visibleRange, listBoundary = _d.listBoundary, rangeTopListHeight = _d.topListHeight, _e = _b[3], scrolledToInitialItem = _e.scrolledToInitialItem, initialTopMostItemIndex = _e.initialTopMostItemIndex, topListHeight = _b[4].topListHeight, stateFlags = _b[5], didMount = _b[6].didMount, recalcInProgress = _b[7].recalcInProgress;
    var topItemsIndexes = u.statefulStream([]);
    var initialItemCount = u.statefulStream(0);
    var itemsRendered = u.stream();
    u.connect(groupedListSystem.topItemsIndexes, topItemsIndexes);
    var listState = u.statefulStreamFromEmitter(u.pipe(u.combineLatest(didMount, recalcInProgress, u.duc(visibleRange, tupleComparator), u.duc(totalCount), u.duc(sizes), u.duc(initialTopMostItemIndex), scrolledToInitialItem, u.duc(topItemsIndexes), u.duc(firstItemIndex), u.duc(gap), data), u.filter(function (_a) {
        var _b = __read(_a, 11), mount = _b[0], recalcInProgress = _b[1], totalCount = _b[3], data = _b[10];
        // When data length changes, it is synced to totalCount, both of which trigger a recalc separately.
        // Recalc should be skipped then, as the calculation expects both data and totalCount to be in sync.
        var dataChangeInProgress = data && data.length !== totalCount;
        return mount && !recalcInProgress && !dataChangeInProgress;
    }), u.map(function (_a) {
        var e_3, _b;
        var _c = __read(_a, 11), _d = __read(_c[2], 2), startOffset = _d[0], endOffset = _d[1], totalCount = _c[3], sizes = _c[4], initialTopMostItemIndex = _c[5], scrolledToInitialItem = _c[6], topItemsIndexes = _c[7], firstItemIndex = _c[8], gap = _c[9], data = _c[10];
        var sizesValue = sizes;
        var sizeTree = sizesValue.sizeTree, offsetTree = sizesValue.offsetTree;
        var initialItemCountValue = u.getValue(initialItemCount);
        if (totalCount === 0) {
            return __assign(__assign({}, EMPTY_LIST_STATE), { totalCount: totalCount });
        }
        // no container measruements yet
        if (startOffset === 0 && endOffset === 0) {
            if (initialItemCountValue === 0) {
                return __assign(__assign({}, EMPTY_LIST_STATE), { totalCount: totalCount });
            }
            else {
                return buildListStateFromItemCount(initialItemCountValue, initialTopMostItemIndex, sizes, firstItemIndex, gap, data || []);
            }
        }
        if (empty(sizeTree)) {
            if (initialItemCountValue > 0) {
                return null;
            }
            var state = buildListState(probeItemSet(getInitialTopMostItemIndexNumber(initialTopMostItemIndex, totalCount), sizesValue, data), [], totalCount, gap, sizesValue, firstItemIndex);
            return state;
        }
        var topItems = [];
        if (topItemsIndexes.length > 0) {
            var startIndex = topItemsIndexes[0];
            var endIndex = topItemsIndexes[topItemsIndexes.length - 1];
            var offset = 0;
            try {
                for (var _e = __values(rangesWithin(sizeTree, startIndex, endIndex)), _f = _e.next(); !_f.done; _f = _e.next()) {
                    var range = _f.value;
                    var size = range.value;
                    var rangeStartIndex = Math.max(range.start, startIndex);
                    var rangeEndIndex = Math.min(range.end, endIndex);
                    for (var i = rangeStartIndex; i <= rangeEndIndex; i++) {
                        topItems.push({ index: i, size: size, offset: offset, data: data && data[i] });
                        offset += size;
                    }
                }
            }
            catch (e_3_1) { e_3 = { error: e_3_1 }; }
            finally {
                try {
                    if (_f && !_f.done && (_b = _e.return)) _b.call(_e);
                }
                finally { if (e_3) throw e_3.error; }
            }
        }
        // If the list hasn't scrolled to the initial item because the initial item was set,
        // render empty list.
        //
        // This is a condition to be evaluated after the probe check cycle, do not merge
        // with the totalCount check above
        if (!scrolledToInitialItem) {
            return buildListState([], topItems, totalCount, gap, sizesValue, firstItemIndex);
        }
        var minStartIndex = topItemsIndexes.length > 0 ? topItemsIndexes[topItemsIndexes.length - 1] + 1 : 0;
        var offsetPointRanges = rangesWithinOffsets(offsetTree, startOffset, endOffset, minStartIndex);
        if (offsetPointRanges.length === 0) {
            return null;
        }
        var maxIndex = totalCount - 1;
        var items = u.tap([], function (result) {
            var e_4, _a;
            try {
                for (var offsetPointRanges_1 = __values(offsetPointRanges), offsetPointRanges_1_1 = offsetPointRanges_1.next(); !offsetPointRanges_1_1.done; offsetPointRanges_1_1 = offsetPointRanges_1.next()) {
                    var range = offsetPointRanges_1_1.value;
                    var point = range.value;
                    var offset = point.offset;
                    var rangeStartIndex = range.start;
                    var size = point.size;
                    if (point.offset < startOffset) {
                        rangeStartIndex += Math.floor((startOffset - point.offset + gap) / (size + gap));
                        var itemCount = rangeStartIndex - range.start;
                        offset += itemCount * size + itemCount * gap;
                    }
                    if (rangeStartIndex < minStartIndex) {
                        offset += (minStartIndex - rangeStartIndex) * size;
                        rangeStartIndex = minStartIndex;
                    }
                    var endIndex = Math.min(range.end, maxIndex);
                    for (var i = rangeStartIndex; i <= endIndex; i++) {
                        if (offset >= endOffset) {
                            break;
                        }
                        result.push({ index: i, size: size, offset: offset, data: data && data[i] });
                        offset += size + gap;
                    }
                }
            }
            catch (e_4_1) { e_4 = { error: e_4_1 }; }
            finally {
                try {
                    if (offsetPointRanges_1_1 && !offsetPointRanges_1_1.done && (_a = offsetPointRanges_1.return)) _a.call(offsetPointRanges_1);
                }
                finally { if (e_4) throw e_4.error; }
            }
        });
        return buildListState(items, topItems, totalCount, gap, sizesValue, firstItemIndex);
    }), 
    //@ts-expect-error filter needs to be fixed
    u.filter(function (value) { return value !== null; }), u.distinctUntilChanged()), EMPTY_LIST_STATE);
    u.connect(u.pipe(data, u.filter(u.isDefined), u.map(function (data) { return data === null || data === void 0 ? void 0 : data.length; })), totalCount);
    u.connect(u.pipe(listState, u.map(function (value) { return value.topListHeight; })), topListHeight);
    u.connect(topListHeight, rangeTopListHeight);
    u.connect(u.pipe(listState, u.map(function (state) { return [state.top, state.bottom]; })), listBoundary);
    u.connect(u.pipe(listState, u.map(function (state) { return state.items; })), itemsRendered);
    var endReached = u.streamFromEmitter(u.pipe(listState, u.filter(function (_a) {
        var items = _a.items;
        return items.length > 0;
    }), u.withLatestFrom(totalCount, data), u.filter(function (_a) {
        var _b = __read(_a, 2), items = _b[0].items, totalCount = _b[1];
        return items[items.length - 1].originalIndex === totalCount - 1;
    }), u.map(function (_a) {
        var _b = __read(_a, 3), totalCount = _b[1], data = _b[2];
        return [totalCount - 1, data];
    }), u.distinctUntilChanged(tupleComparator), u.map(function (_a) {
        var _b = __read(_a, 1), count = _b[0];
        return count;
    })));
    var startReached = u.streamFromEmitter(u.pipe(listState, u.throttleTime(200), u.filter(function (_a) {
        var items = _a.items, topItems = _a.topItems;
        return items.length > 0 && items[0].originalIndex === topItems.length;
    }), u.map(function (_a) {
        var items = _a.items;
        return items[0].index;
    }), u.distinctUntilChanged()));
    var rangeChanged = u.streamFromEmitter(u.pipe(listState, u.filter(function (_a) {
        var items = _a.items;
        return items.length > 0;
    }), u.map(function (_a) {
        var items = _a.items;
        var startIndex = 0;
        var endIndex = items.length - 1;
        while (items[startIndex].type === 'group' && startIndex < endIndex) {
            startIndex++;
        }
        while (items[endIndex].type === 'group' && endIndex > startIndex) {
            endIndex--;
        }
        return {
            startIndex: items[startIndex].index,
            endIndex: items[endIndex].index,
        };
    }), u.distinctUntilChanged(rangeComparator)));
    return __assign({ listState: listState, topItemsIndexes: topItemsIndexes, endReached: endReached, startReached: startReached, rangeChanged: rangeChanged, itemsRendered: itemsRendered, initialItemCount: initialItemCount }, stateFlags);
}, u.tup(sizeSystem, groupedListSystem, sizeRangeSystem, initialTopMostItemIndexSystem, scrollToIndexSystem, stateFlagsSystem, propsReadySystem, recalcSystem), { singleton: true });
